"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.residentPageSchema = exports.residentPickPathSchema = exports.facilityIdPathSchema = exports.residentListQuerySchema = exports.residentSummarySchema = exports.DEPARTURE_DATE_SOURCES = void 0;
/**
 * `RPM-097` — the shape `GET /api/rpm/v1/facilities/:facilityId/residents` accepts and returns (`J-214`).
 *
 * NO `mrn`, ANYWHERE. The block is unusually explicit: "MRN is never displayed anywhere in ChartVN
 * and is never used as a key: it is read from the mirror, and it reaches no screen, no response body
 * and no log line." Acceptance row 8 makes it a check rather than a promise — `no-mrn-as-key` fails
 * on a response type that declares one. An MRN on the option line would add an identifier to a list
 * a nurse reads over a shoulder without helping her choose.
 *
 * THE IDENTITY IS IN THE BODY AND NOWHERE ELSE. `.claude/rules/phi.md` rule 2 keeps a resident's
 * name, birth date and id out of paths and query strings, so the path names the BUILDING and the
 * query carries one boolean and a page size. The resident's id reaches the server in a body when a
 * reading is saved, never in a URL.
 *
 * NO AGE FIELD. The Operator surface row puts an age beside the birth date "so a birth date does not
 * have to be turned into an age in a nurse's head", and says in the same breath that it is "computed
 * at render time and never persisted" — a stored age is wrong the day after it is written, and a
 * transmitted one is wrong whenever the client's clock and the server's disagree about the day.
 */
const zod_1 = require("zod");
const resident_status_js_1 = require("./resident-status.js");
/** The mirror's identifier width. `carease.patients.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/**
 * Where a departed resident's date on the option line came from.
 *
 * The block fixes both the rule and its wording: the departure date is
 * `COALESCE(discharge_date, deceased_date, updated_at)`, and "where the date is `updated_at` the line
 * says **last updated** before it, so a record's last update is never presented as a discharge date".
 * The server resolves which of the three it used, because the same expression decides whether the
 * resident is inside the 30-day window at all — one rule, one place.
 */
exports.DEPARTURE_DATE_SOURCES = ["DISCHARGE", "DECEASED", "LAST_UPDATED"];
/**
 * One resident, as the roster shows her.
 *
 * NAME, BIRTH DATE AND UNIT / ROOM / BED TRAVEL TOGETHER, and acceptance row 2 is why: two residents
 * in one building can share a name, and "birth date and bed are what tell them apart". Dropping any
 * of them would leave the nurse choosing between two identical lines — which is `NE-01`, an alert
 * routed to the wrong person.
 */
exports.residentSummarySchema = zod_1.z
    .object({
    careasePatientId: MIRROR_ID,
    /**
     * NULLABLE, because the mirror's column is (`external.ts:30`).
     *
     * A resident the source system holds with no name is a real state, and a contract that promised
     * a string would either invent one or drop her from the roster — and a resident missing from the
     * list is a resident nobody can enter a reading against. The surface says what the mirror holds;
     * the birth date and the bed still tell her apart, which is what acceptance row 2 relies on.
     */
    name: zod_1.z.string().nullable(),
    /** The mirror's `birthdate` as a date, or null where it holds none. */
    birthDate: zod_1.z.string().nullable(),
    /**
     * The mirror's own word for it, not a code (`external.ts:34`, varchar(20)), or null.
     *
     * A DELIBERATE WIDENING, asked for by the project owner on 2026-09-22 (`J-461`). This contract
     * and the repo's column map list mirror columns one at a time precisely so a new one never
     * reaches a screen because somebody selected `*`. Adding a column here is a decision each time.
     */
    gender: zod_1.z.string().nullable(),
    floor: zod_1.z.string().nullable(),
    unit: zod_1.z.string().nullable(),
    room: zod_1.z.string().nullable(),
    bed: zod_1.z.string().nullable(),
    // ---- the four status facts `RPM-084` and `RPM-085` read ----
    /** The mirror's own value: `Current`, `Discharged`, `Pending`, `Suspended`, `New` or null. */
    status: zod_1.z.string().nullable(),
    dischargeDate: zod_1.z.string().nullable(),
    deceasedDate: zod_1.z.string().nullable(),
    /** `RPM-084`: hospice marks someone LIVING in the building, never a departure. */
    isHospice: zod_1.z.boolean(),
    /**
     * `RPM-084`: the day the mirror set hospice (`hospice_set_date`), which the banner states as
     * "Hospice since". Null where the mirror holds none — the banner then says the date is not
     * recorded — and on a resident who is not on hospice. Never the departure date: a hospice
     * resident is Current and has none (`J-306`).
     */
    hospiceSetDate: zod_1.z.string().nullable(),
    /**
     * Set only on a resident the toggle added. Null on a `Current` resident, whose option line
     * carries no departure at all.
     */
    departureDate: zod_1.z.string().nullable(),
    departureDateSource: zod_1.z.enum(exports.DEPARTURE_DATE_SOURCES).nullable(),
    /**
     * `RPM-085`: the warning the mirror's status facts call for — Deceased, Discharged, or the mirror
     * not saying she is Current — or null for a Current resident. A first-class field, as the block's
     * API row asks, read by Enter vitals to warn before any contact is offered (`J-307`).
     */
    statusWarning: resident_status_js_1.residentStatusWarningSchema.nullable(),
    /**
     * The care unit the entry form pre-selects (`RPM-097`, `CR-43`): the one active unit whose label
     * or mirror values equal her mirror `unit` after normalising (`suggestCareUnit`). Null when none
     * or several match — the nurse chooses.
     */
    suggestedCareUnitId: zod_1.z.uuid().nullable(),
})
    .meta({ id: "ResidentSummary", description: "One resident as the roster lists her" });
/**
 * The query.
 *
 * `includeDeparted` IS A BOOLEAN AND CARRIES NO IDENTIFIER, which the block's API row states
 * outright. It changes which residents can be picked and nothing after the pick.
 */
exports.residentListQuerySchema = zod_1.z
    .strictObject({
    /** Off by default: the roster is the building's `Current` residents. */
    includeDeparted: zod_1.z.stringbool().default(false),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(100),
    cursor: MIRROR_ID.optional(),
})
    .meta({ description: "One building's roster, and whether it includes recent departures" });
/** The roster's path parameter: a building, never a person. */
exports.facilityIdPathSchema = zod_1.z.object({ facilityId: MIRROR_ID });
/**
 * The pick's path: one building and one resident.
 *
 * The resident's id is an opaque mirror key, which is what `.claude/rules/phi.md` rule 2 permits in
 * a path — "route params and query strings carry opaque UUIDs only. No names, dates of birth, MRNs".
 * `carease.patients.id` is that kind of key, and `/api/v1/patients/:patientId` already carries one.
 */
exports.residentPickPathSchema = zod_1.z.object({
    facilityId: MIRROR_ID,
    careasePatientId: MIRROR_ID,
});
exports.residentPageSchema = zod_1.z
    .object({
    items: zod_1.z.array(exports.residentSummarySchema),
    nextCursor: MIRROR_ID.nullable(),
})
    .meta({ id: "ResidentPage", description: "One page of a building's roster" });
//# sourceMappingURL=rpm-residents.js.map